import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { translate, currencies } from "@/i18n";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = user
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "it");
  const [config, setConfig] = useState({});
  // Entitlement verification state: null=checking, true/false=verified,
  // "error"=status unavailable (network/server failure). "error" NEVER grants
  // access: product entry remains blocked until a successful re-verification.
  const [entitled, setEntitled] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const initializeAuth = async () => {
      const url = new URL(window.location.href);
      const launchTicket = url.searchParams.get("launch");

      if (launchTicket) {
        url.searchParams.delete("launch");
        window.history.replaceState(
          {},
          document.title,
          `${url.pathname}${url.search}${url.hash}`
        );

        try {
          const r = await api.post("/auth/platform-launch", {
            ticket: launchTicket,
          });

          if (cancelled) return;

          if (r.data?.language) {
            localStorage.setItem("lang", r.data.language);
            setLang(r.data.language);
          }

          setUser(r.data);
          return;
        } catch (_) {
          try {
            await api.post("/auth/logout");
          } catch (_) {}

          if (!cancelled) setUser(false);
          return;
        }
      }

      try {
        const r = await api.get("/auth/me");
        if (!cancelled) setUser(r.data);
      } catch (_) {
        if (!cancelled) setUser(false);
      }
    };

    initializeAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (user && user.language && user.language !== lang) setLang(user.language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const refreshEntitlement = useCallback(async () => {
    if (!user || !user.id) { setEntitled(null); return; }
    setEntitled(null); // checking
    try {
      const r = await api.get("/entitlement");
      setEntitled(!!r.data.access);
    } catch (_) {
      setEntitled("error"); // unavailable: fail closed, retry offered
    }
  }, [user]);

  useEffect(() => {
    if (user && user.id) {
      refreshEntitlement();
    } else if (user === false) {
      setEntitled(null);
    }
  }, [user, refreshEntitlement]);

  const t = useCallback((key, params) => translate(lang, key, params), [lang]);

  const currency = (user && user.currency) || localStorage.getItem("currency") || "EUR";
  const symbol = currencies.find((c) => c.code === currency)?.symbol || "€";
  const fmt = useCallback(
    (v) => {
      const n = Number.isFinite(Number(v)) ? Number(v) : 0;
      return `${symbol}${n.toFixed(2)}`;
    },
    [symbol]
  );

  const changeLanguage = useCallback(
    async (code) => {
      setLang(code);
      localStorage.setItem("lang", code);
      if (user) {
        try {
          const r = await api.put("/profile", { language: code });
          setUser(r.data);
        } catch (_) {}
      }
    },
    [user]
  );

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setUser(r.data);
    return r.data;
  };

  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    setUser(r.data);
    return r.data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    setUser(false);
  };

  const updateProfile = async (updates) => {
    const r = await api.put("/profile", updates);
    setUser(r.data);
    return r.data;
  };

  const changePassword = async (current_password, new_password) => {
    await api.post("/auth/change-password", { current_password, new_password });
  };

  const deleteAccount = async (password) => {
    await api.delete("/account", { data: { password } });
    setUser(false);
  };

  const exportData = async () => {
    const r = await api.get("/account/export");
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "360nails-my-data.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, lang, t, currency, symbol, fmt, changeLanguage, login, register, logout,
               updateProfile, changePassword, deleteAccount, exportData, config, entitled, refreshEntitlement, apiError }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
